import {
  Alert,
  Button,
  Card,
  Center,
  Group,
  PasswordInput,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle, } from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SignedIn, SignedOut } from '@clerk/clerk-react';
import { ClerkSignInForm } from '../auth/ClerkSignInForm.js';
import { Navigate } from 'react-router-dom';
import { useAuthConfig } from '../auth/AuthGate.js';
import { api } from '../lib/api.js';
import { Logo } from '../components/Logo.js';

/**
 * Clerk handles the authentication; the form is ours.
 *
 * Using their headless hooks rather than <SignIn /> keeps the page in our own
 * design system, with no third-party markup or "Secured by Clerk" footer.
 */
function ClerkSignIn() {
  return (
    <Center mih="100vh" p="md">
      <Stack w={420} gap="lg">
        <Group gap="sm" justify="center">
          <div>
            <Logo height={30} />
            <Text size="sm" c="dimmed" mt={8}>
              Dealership integration console
            </Text>
          </div>
        </Group>

        <SignedOut>
          <ClerkSignInForm />
        </SignedOut>
        <SignedIn>
          <Navigate to="/app" replace />
        </SignedIn>
      </Stack>
    </Center>
  );
}

export function LoginPage() {
  const authConfig = useAuthConfig();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const form = useForm({
    initialValues: { email: '', password: '' },
    validate: {
      email: (v) => (/^\S+@\S+\.\S+$/.test(v) ? null : 'Enter a valid email address'),
      password: (v) => (v.length > 0 ? null : 'Password is required'),
    },
  });

  async function submit(values: { email: string; password: string }) {
    setBusy(true);
    setError('');
    try {
      await api.post('/admin/login', values);
      await qc.invalidateQueries({ queryKey: ['me'] });
      navigate('/app', { replace: true });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (authConfig?.provider === 'clerk') {
    return <ClerkSignIn />;
  }

  return (
    <Center mih="100vh" p="md">
      <Stack w={400} gap="lg">
        <Group gap="sm" justify="center">
          <div>
            <Logo height={30} />
            <Text size="sm" c="dimmed" mt={8}>
              Dealership integration console
            </Text>
          </div>
        </Group>

        <Card shadow="sm">
          <form onSubmit={form.onSubmit(submit)}>
            <Stack>
              <TextInput
                label="Email"
                placeholder="you@agency.com"
                autoComplete="username"
                required
                {...form.getInputProps('email')}
              />
              <PasswordInput
                label="Password"
                autoComplete="current-password"
                required
                {...form.getInputProps('password')}
              />

              {error && (
                <Alert color="red" icon={<IconAlertCircle size={16} />} variant="light">
                  {error}
                </Alert>
              )}

              <Button type="submit" loading={busy} fullWidth mt="xs">
                Sign in
              </Button>
            </Stack>
          </form>
        </Card>

        <Text size="xs" c="dimmed" ta="center">
          Accounts are created by an administrator.
        </Text>
      </Stack>
    </Center>
  );
}
